import { initialData, type UserClientState } from '@/types';
import { setByPath } from '@/lib';
import { createSlice } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '@/services/axios-base-query';
import { CLIENT_ROUTES } from '@/constants';
import type { OrganizationMembershipResponse } from '@/types';

export interface PlanModuleCost {
  id?: number;
  plan_name?: string | null;
  module_name?: string;
  module_code: string;
  cost?: string;
  currency?: string;
  created_at?: string;
  plan?: number;
  module?: number;
}

export interface ClientPlanDetail {
  plan_modules_cost?: PlanModuleCost[];
  plan_name?: string | null;
  plan_details?: string | null;
  created_at?: string;
  maximum_users?: number;
  features?: unknown[];
}

export interface ClientPlan {
  id?: number;
  plan_name?: string | null;
  created_at?: string;
  latest?: boolean;
  active_users?: number;
  modules?: number[];
  features?: unknown[];
  client?: number;
  plan?: ClientPlanDetail;
}

export interface ClientStats {
  number_of_users?: number;
}

export interface OrganizationProfile {
  id: number;
  profile_setup?: boolean;
  name?: string;
  created_on?: string;
  slogan?: string;
  email?: string;
  about_us?: string;
  website?: string;
  address?: string;
  logo_url?: string | null;
  tenant_type?: string;
  tenant_id?: number;
  telephone?: string;
  country?: string | null;
}

export interface ClientScopeItem {
  id: number;
  name: string;
  description?: string;
  is_approved?: boolean;
}

export interface ClientScopeResponse {
  id: number;
  client_scope?: ClientScopeItem[];
  logo_url?: string | null;
  banner_url?: string | null;
  schema_name?: string;
  name?: string;
  created_on?: string;
  tenant_type?: string;
  organization_type?: string;
}

type ClientScopePayload = {
  client_scope: Array<{ add_id: number }>;
};

export const userClientApi = createApi({
  reducerPath: 'ClientApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: [
    'UserOrganizations',
    'ClientPlans',
    'ClientStats',
    'ClientProfile',
    'ClientScope',
  ],
  endpoints: (builder) => ({
    // Hook to get User Organizations (which contains Roles)
    getClientOrganizations: builder.query<OrganizationMembershipResponse, void>({
      query: () => ({
        url: CLIENT_ROUTES.GET_USER_ORGANIZATIONS(),
        method: 'GET',
      }),
      providesTags: ['UserOrganizations'],
    }),

    // Hook to get specific Client Plans
    getClientPlans: builder.query<ClientPlan[], { tenant: string }>({
      query: ({ tenant }) => ({
        url: CLIENT_ROUTES.GET_CLIENT_PLANS(tenant),
        method: 'GET',
      }),
      providesTags: ['ClientPlans'],
    }),
    getClientStats: builder.query<ClientStats, { tenant: string }>({
      query: ({ tenant }) => ({
        url: CLIENT_ROUTES.GET_CLIENT_STATS(tenant),
        method: 'GET',
      }),
      providesTags: ['ClientStats'],
    }),
    getOrganizationProfile: builder.query<OrganizationProfile, { tenant: string }>(
      {
        query: ({ tenant }) => ({
          url: CLIENT_ROUTES.GET_CLIENT_PROFILE(tenant),
          method: 'GET',
        }),
        providesTags: ['ClientProfile'],
      }
    ),
    getClientScope: builder.query<
      ClientScopeResponse,
      { tenant: string; clientId: number }
    >({
      query: ({ tenant, clientId }) => ({
        url: CLIENT_ROUTES.GET_CLIENT_SCOPE(tenant, clientId),
        method: 'GET',
      }),
      providesTags: ['ClientScope'],
    }),
    updateClientScope: builder.mutation<
      ClientScopeResponse,
      { tenant: string; clientId: number; data: ClientScopePayload }
    >({
      query: ({ tenant, clientId, data }) => ({
        url: CLIENT_ROUTES.GET_CLIENT_SCOPE(tenant, clientId),
        method: 'PATCH',
        data,
      }),
      invalidatesTags: ['ClientScope'],
    }),
    removeClientScope: builder.mutation<
      ClientScopeResponse,
      { tenant: string; clientId: number; data: ClientScopePayload }
    >({
      query: ({ tenant, clientId, data }) => ({
        url: CLIENT_ROUTES.REMOVE_CLIENT_SCOPE(tenant, clientId),
        method: 'PATCH',
        data,
      }),
      invalidatesTags: ['ClientScope'],
    }),
    getScopeOptions: builder.query<ClientScopeItem[], { tenant: string }>({
      query: ({ tenant }) => ({
        url: CLIENT_ROUTES.GET_SCOPE_OPTIONS(tenant),
        method: 'GET',
      }),
    }),

    getUserRoles: builder.query<string[], { tenant: string }>({
      query: () => ({
        url: CLIENT_ROUTES.GET_USER_ORGANIZATIONS(),
        method: 'GET',
      }),
      transformResponse: (
        response: OrganizationMembershipResponse,
        _meta,
        { tenant }
      ) => {
        const selectedOrganization = response?.find(
          (org) => org?.client?.schema_name === tenant
        );

        return selectedOrganization ? selectedOrganization.user_roles : [];
      },
      providesTags: ['UserOrganizations'],
    }),
  }),
});

export const {
  useGetClientOrganizationsQuery,
  useGetClientPlansQuery,
  useGetClientStatsQuery,
  useGetOrganizationProfileQuery,
  useGetClientScopeQuery,
  useUpdateClientScopeMutation,
  useRemoveClientScopeMutation,
  useGetScopeOptionsQuery,
  useGetUserRolesQuery
} = userClientApi;

const initialState: UserClientState = {
  wizardData: {
    organization: {
      ...initialData.organization,
    },
    skills: {
      ...initialData.skills,
    },
    profile: {
      ...initialData.profile,
    },
    orgLogo: '',
    board: {
      ...initialData.board,
    },
  },
  wizardStep: 1,
  sound: false,
};

export const userClientSlice = createSlice({
  name: 'clientDetails',
  initialState,
  reducers: {
    clearClientSideCacheStates: () => initialState,
    setwizardData: (state, action) => {
      state.wizardData = action.payload;
    },
    clearwizardData: (state) => {
      state.wizardData = initialState.wizardData;
    },
    updatewizardData: (state, action) => {
      state.wizardData = {
        ...state.wizardData,
        ...action.payload,
      };
    },
    updatewizardDataField: (state, action) => {
      const { field, value } = action.payload;
      const updatedValue = value === undefined ? null : value;
      if (!state.wizardData) return;
      // Update the state with the safely handled value
      setByPath(state.wizardData, field, updatedValue);
      /**
 * Set the wizard step to the specified value.
 * If the action is 'next' and the current step is 2 or greater, do nothing.
 * If the action is 'prev' and the current step is 1 or less, do nothing.
/*******  9a253622-f64c-45f9-b11b-50b71ba036ec  *******/
    },

    setWizardStep: (state, action) => {
      if (action.payload.action === 'next' && state.wizardStep >= 2) return;
      if (action.payload.action === 'prev' && state.wizardStep <= 1) return;
      state.wizardStep = action.payload.step;
    },
    clearWizardStep: (state) => {
      state.wizardStep = initialState.wizardStep;
    },

    setSound: (state, action) => {
      state.sound = action.payload;
    },
  },
});

const {
  clearClientSideCacheStates,
  clearwizardData,
  setwizardData,
  updatewizardData,
  updatewizardDataField,
  clearWizardStep,
  setWizardStep,
  setSound,
} = userClientSlice.actions;

export {
  clearClientSideCacheStates,
  clearwizardData,
  setwizardData,
  updatewizardData,
  updatewizardDataField,
  clearWizardStep,
  setWizardStep,
  setSound,
};
